import 'ol/ol.css';
import Map from 'ol/Map';
import View from 'ol/View';
import XYZ from 'ol/source/XYZ';
import TileLayer from 'ol/layer/Tile';
import { getCenter, getWidth, getHeight } from 'ol/extent';
import MVT from 'ol/format/MVT';
import VectorTileLayer from 'ol/layer/VectorTile';
import VectorTileSource from 'ol/source/VectorTile';
import LayerGroup from 'ol/layer/Group';
import LayerSwitcher from 'ol-layerswitcher';
import 'ol-layerswitcher/dist/ol-layerswitcher.css';
import '../../assets/layerswitcher.css';

import store from '../../store';
import { setZoom } from '../../reducers/mainControls';

import config from '../../config/mapSetup';
import { layerConfig } from '../../config/layer-config';
import { createPolygonRampStyler, createPolygonUniqueStyler, createPointUniqueStyler } from './layer-styler';
import { addSelectionLayerToMap } from './selection';
import { addAddressMarkerLayerToMap } from './address-marker';

const { initialExtent, maxZoom, basemaps } = config;

const createLayer = (config) => {
  let style;
  const { geometryType, field, ramp, unique, size, strokeColor } = config;

  if (geometryType === 'polygon') {
    if (ramp) {
      style = createPolygonRampStyler(field, ramp, strokeColor);
    }

    if (unique) {
      style = createPolygonUniqueStyler(field, unique, strokeColor);
    }
  }

  if (geometryType === 'point') {
    if (unique) {
      style = createPointUniqueStyler(field, unique, size, strokeColor);
    }
  }

  const { tileUrl, visible } = config;

  const { cacheVersion } = store.getState();
  const source = new VectorTileSource({ format: new MVT(), url: `${tileUrl}?v=${cacheVersion}` });
  const layer = new VectorTileLayer({ className: 'vt-layer', source, style, visible, extent: initialExtent });

  const { theme, level, title, minZoom } = config;
  layer.mbi = theme && level ? { theme, level } : { title };

  if (minZoom) {
    layer.setMinZoom(minZoom - 1);
  }

  return layer;
};

const streetsSwitchLevel = 12;
const cartoLayer = new TileLayer({ source: new XYZ(basemaps.carto), visible: true, maxZoom: streetsSwitchLevel });
const streetsLayer = new TileLayer({ source: new XYZ(basemaps.streets), visible: true, minZoom: streetsSwitchLevel });
const structuresLayer = new TileLayer({ source: new XYZ(basemaps.structures), visible: true, minZoom: streetsSwitchLevel });
const streetMap = new LayerGroup({ title: 'Street Map', type: 'base', combine: true, layers: [cartoLayer, structuresLayer, streetsLayer] });

const aerialMap = new TileLayer({ title: 'Aerial Imagery', type: 'base', source: new XYZ(basemaps.imagery), visible: false });

let map;

export const getMap = () => map;

export const zoomToExtent = (extent) => {
  const scale = 1.05;

  const center = getCenter(extent);
  const halfWidth = getWidth(extent) * 0.5 * scale;
  const halfHeight = getHeight(extent) * 0.5 * scale;

  const scaledExtent = [center[0] - halfWidth, center[1] - halfHeight, center[0] + halfWidth, center[1] + halfHeight];
  map.getView().fit(scaledExtent, map.getSize());
};

export const createMap = ({ div, currentUser }) => {
  const isUserSignedIn = !!currentUser.name;

  const view = new View({
    constrainResolution: true,
  });

  const rect = div.getBoundingClientRect();
  const resolution = view.getResolutionForExtent(initialExtent, [rect.width, rect.height]);
  const zoom = Math.floor(view.getZoomForResolution(resolution));

  const dataLayers = layerConfig.filter((c) => !c.restricted || isUserSignedIn).map((c) => createLayer(c));

  view.setZoom(zoom);
  view.setMaxZoom(maxZoom + 1);
  view.setCenter(getCenter(initialExtent));

  view.on('change:resolution', () => {
    store.dispatch(setZoom(view.getZoom()));
  });

  map = new Map({
    layers: [aerialMap, streetMap, ...dataLayers],
    view,
  });

  addSelectionLayerToMap(map);
  addAddressMarkerLayerToMap(map);

  const layerSwitcher = new LayerSwitcher({ groupSelectStyle: 'group' });
  map.addControl(layerSwitcher);

  map.setTarget(div);
  return map;
};

export const showLayer = ({ theme, level }) => {
  const map = getMap();

  map.getLayers().forEach((layer) => {
    if (layer.mbi) {
      if (layer.mbi.theme) {
        layer.setVisible(layer.mbi.theme === theme && layer.mbi.level === level);
      } else if (layer.mbi.title === 'Federal Funding Areas') {
        layer.setVisible(theme === 'Federal Funding');
      }
    }
  });
};
